import { ReactNode } from "react";
import { Box, styled } from "@mui/material";

import { AddressLink } from "../AddressLink";
import { humanizeToken } from "../DigitalSignature/TemplateVarsForm";
import { ACCENT, INK, MUTED, SectionLabel } from "../Settlement/settlementStyles";
import { ApplicantRow, NativeKycResult } from "./types";

// The read-only half of the applicant review: everything the verifier submitted, laid out as
// white cards on the popup's grey sheet (the settlement dialogs' anatomy). Every value goes
// through one "missing → N/A" rule so an absent field can never render as "undefined".

export const NOT_AVAILABLE = "N/A";

const isBlank = (value: unknown) => value == null || (typeof value === "string" && value.trim() === "");

const display = (value: unknown): string => {
  if (isBlank(value)) {
    return NOT_AVAILABLE;
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
};

// face_match.similarity_score is already on a 0–100 scale — it only needs its unit.
export const formatSimilarityScore = (score: unknown): string => (isBlank(score) ? NOT_AVAILABLE : `${score}%`);

const formatDate = (iso?: string) => {
  if (!iso) {
    return NOT_AVAILABLE;
  }
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? NOT_AVAILABLE : date.toLocaleDateString(undefined, { dateStyle: "medium" });
};

// "KYC_VERIFIED" → "Verified", "submitted" → "Submitted".
const formatStatus = (status?: string | null) =>
  isBlank(status) ? NOT_AVAILABLE : humanizeToken(String(status).replace(/^KYC_/, ""));

const providerVerdict = (kyc?: NativeKycResult | null) => {
  if (kyc?.kycPassed === true) {
    return "Passed";
  }
  if (kyc?.kycPassed === false) {
    return "Failed";
  }
  return formatStatus(kyc?.status);
};

// ── Layout ───────────────────────────────────────────────────────────────────────────────────

const Card = styled("div")`
  background-color: #fff;
  border-radius: 20px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  /* SectionLabel's own 16px foot is sized for a panel heading; inside a card the gap is enough. */
  & > p:first-of-type {
    margin-bottom: 4px;
  }
`;

// Label column on the left on desktop; stacked on a phone, where 160px of label would leave the
// value a sliver.
const RowRoot = styled("div")`
  display: grid;
  grid-template-columns: 160px minmax(0, 1fr);
  gap: 12px;
  align-items: baseline;

  @media (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 2px;
  }
`;

const RowLabel = styled("span")`
  font-family: "Inter", sans-serif;
  font-size: 13px;
  line-height: 18px;
  color: ${MUTED};
`;

const RowValue = styled("span")`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  line-height: 18px;
  color: ${INK};
  word-break: break-word;

  & a {
    color: ${INK};
  }
`;

const Note = styled("p")`
  font-family: "Inter", sans-serif;
  font-size: 12px;
  line-height: 16px;
  color: ${MUTED};
  margin: 4px 0 0;
`;

const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <RowRoot>
    <RowLabel>{label}</RowLabel>
    <RowValue>{children}</RowValue>
  </RowRoot>
);

const Address = ({ address }: { address?: string | null }) =>
  address ? <AddressLink address={address} type="user" copy sx={{ all: "unset" }} /> : <>{NOT_AVAILABLE}</>;

const ExternalLink = ({ href, label }: { href?: string | null; label: string }) =>
  href ? (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {label}
    </a>
  ) : (
    <>{NOT_AVAILABLE}</>
  );

// A provider sub-record (fraud signals, selfie checks, extracted ID fields) as label/value rows.
const RecordRows = ({ record }: { record?: Record<string, unknown> | null }) => {
  const entries = Object.entries(record ?? {});
  if (entries.length === 0) {
    return <RowValue>{NOT_AVAILABLE}</RowValue>;
  }
  return (
    <>
      {entries.map(([key, value]) => (
        <Row key={key} label={humanizeToken(key)}>
          {display(value)}
        </Row>
      ))}
    </>
  );
};

// ── Likeness score ─────────────────────────────────────────────────────────────────────────

// The one number the approval turns on, so it gets the blue record card rather than a row.
const LikenessCard = styled("div")`
  background-color: ${ACCENT};
  border-radius: 20px;
  padding: 16px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const LikenessValue = styled("span")`
  font-family: "IBM Plex Mono", "IBM Mono", monospace;
  font-weight: 600;
  font-size: 28px;
  line-height: 32px;
  color: ${INK};
`;

// ── Sections ───────────────────────────────────────────────────────────────────────────────

export const VerifierDetails = ({ applicant }: { applicant: ApplicantRow }) => {
  const kyc = applicant.nativeKYCStatus;
  const failureReasons = (kyc?.failureReasons ?? []).filter((reason) => !isBlank(reason));

  return (
    <>
      <Card>
        <SectionLabel>Verifier</SectionLabel>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px", mb: "4px" }}>
          {applicant.profilePicture ? (
            <Box
              component="img"
              src={applicant.profilePicture}
              alt=""
              sx={{ width: 56, height: 56, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
            />
          ) : (
            <Box sx={{ width: 56, height: 56, borderRadius: "50%", backgroundColor: "#f2f2f2", flexShrink: 0 }} />
          )}
          <RowValue sx={{ fontWeight: 700 }}>{display(applicant.name)}</RowValue>
        </Box>
        <Row label="Patronymic">{display(applicant.patronymic)}</Row>
        <Row label="Email">
          {display(applicant.email)}
          {!isBlank(applicant.email) && ` (${applicant.emailVerified ? "verified" : "not verified"})`}
        </Row>
        <Row label="Phone">{display(applicant.phone)}</Row>
        <Row label="Country">{display(applicant.country)}</Row>
        <Row label="Wallet">
          <Address address={applicant.address} />
        </Row>
        <Row label="Invited by">
          <Address address={applicant.referrerAddress} />
        </Row>
        <Row label="Profile picture">{applicant.profilePicture ? "Provided" : NOT_AVAILABLE}</Row>
        <Row label="Applied">{formatDate(applicant.createdAt)}</Row>
      </Card>

      <LikenessCard>
        <Box>
          <RowLabel>KYC likeness score</RowLabel>
          <Box>
            <LikenessValue>{formatSimilarityScore(kyc?.faceMatch?.similarity_score)}</LikenessValue>
          </Box>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <RowLabel>Face match</RowLabel>
          <Box>
            <RowValue sx={{ fontWeight: 700 }}>{display(kyc?.faceMatch?.matched)}</RowValue>
          </Box>
        </Box>
      </LikenessCard>

      <Card>
        <SectionLabel>Identity verification</SectionLabel>
        <Row label="KYC status">{formatStatus(applicant.kycStatus)}</Row>
        <Row label="Provider result">{providerVerdict(kyc)}</Row>
        <Row label="Failure reasons">{failureReasons.length ? failureReasons.join("; ") : NOT_AVAILABLE}</Row>
      </Card>

      <Card>
        <SectionLabel>Fraud signals</SectionLabel>
        <RecordRows record={kyc?.fraudSignals} />
      </Card>

      <Card>
        <SectionLabel>ID document (extracted)</SectionLabel>
        <RecordRows record={kyc?.documentData} />
        <Note>
          The selfie and ID images are held by the KYC provider and are not available here — these are the details it
          read from the document.
        </Note>
      </Card>

      <Card>
        <SectionLabel>Selfie checks</SectionLabel>
        <RecordRows record={kyc?.selfieValidation} />
      </Card>
    </>
  );
};

export const OrganizationDetails = ({ applicant }: { applicant: ApplicantRow }) => (
  <Card>
    <SectionLabel>Organisation</SectionLabel>
    <Row label="Name">{display(applicant.orgName)}</Row>
    <Row label="Type">{display(applicant.orgType)}</Row>
    <Row label="Address">{display(applicant.orgAddress)}</Row>
    <Row label="Country">{display(applicant.orgCountry)}</Row>
    <Row label="Website">
      <ExternalLink href={applicant.orgWebsite} label={display(applicant.orgWebsite)} />
    </Row>
    <Row label="Phone">{display(applicant.orgPhone)}</Row>
    <Row label="Email">{display(applicant.orgEmail)}</Row>
    <Row label="Status">
      {applicant.orgVerified === false ? "Awaiting approval" : applicant.orgVerified ? "Approved" : NOT_AVAILABLE}
    </Row>
    <Row label="Proof of address">
      <ExternalLink href={applicant.orgProofOfAddress} label="View document" />
    </Row>
  </Card>
);
